import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell, LabelList,
} from 'recharts';
import { formatSwissAmount } from '../billing/formatSwiss';
import { deriveCashSeries } from '../engine/cashSeries';
import { useP } from '../../../i18n/useP';
import './SynthesisTab.css';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// All amounts on the Cash Outlook are displayed in kCHF (whole thousands).
const toK = (v) => Math.round(num(v) / 1000);
const fmtK = (v) => formatSwissAmount(toK(v));

// Vues exclusives du graphique. Chaque vue = un sous-ensemble de flux (barres)
// + une courbe de solde RECALCULÉE depuis le cash initial avec ces seuls flux.
// `side` 'enc' = entrée (+ sur le solde), 'dec' = sortie (− sur le solde).
const VIEWS = [
  { key: 'total', label: 'Total', color: '#2B203E', bars: [
    { key: 'enc', name: 'Encaissements', fill: '#9DC076', stroke: '#5C7A3E', op: 0.5, side: 'enc' },
    { key: 'dec', name: 'Décaissements', fill: '#E6B5A6', stroke: '#BA5D4A', op: 0.5, side: 'dec' },
  ] },
  { key: 'marge-prest', label: 'Marge prestations', color: '#5C7A3E', bars: [
    { key: 'encServices', name: 'Encaissements prestations', fill: '#9DC076', stroke: '#5C7A3E', op: 0.75, side: 'enc' },
    { key: 'decPersonnel', name: 'Charges de personnel', fill: '#E6B5A6', stroke: '#BA5D4A', op: 0.75, side: 'dec' },
  ] },
  { key: 'marge-march', label: 'Marge marchandises', color: '#B8862A', bars: [
    { key: 'encGoods', name: 'Encaissements marchandises', fill: '#D4A85A', stroke: '#B8862A', op: 0.75, side: 'enc' },
    { key: 'cogsMarch', name: 'Coût marchandise', fill: '#E6B5A6', stroke: '#BA5D4A', op: 0.7, side: 'dec' },
  ] },
];
const viewNet = (view, d) => view.bars.reduce(
  (acc, b) => acc + (b.side === 'enc' ? num(d[b.key]) : -num(d[b.key])), 0);

function NumCell({ value, tone }) {
  const k = toK(value);
  if (k === 0) return <td className="synth-table__cell synth-table__cell--zero">—</td>;
  const cls = tone === 'auto'
    ? (k < 0 ? 'is-neg' : 'is-pos')
    : tone === 'neg' ? 'is-neg'
    : tone === 'pos' ? 'is-pos'
    : tone === 'navy' ? 'is-navy'
    : '';
  return (
    <td className={`synth-table__cell ${cls}`}>
      {formatSwissAmount(Math.abs(k))}
    </td>
  );
}

function SignedCell({ value, tone, showSign = false }) {
  const k = toK(value);
  if (k === 0) return <td className="synth-table__cell synth-table__cell--zero">—</td>;
  const cls = tone === 'auto'
    ? (k < 0 ? 'is-neg' : 'is-pos')
    : tone === 'neg' ? 'is-neg'
    : tone === 'pos' ? 'is-pos'
    : tone === 'navy' ? 'is-navy'
    : '';
  const sign = k < 0 ? '-' : (showSign && k > 0 ? '+' : '');
  return (
    <td className={`synth-table__cell ${cls}`}>
      {sign}{formatSwissAmount(Math.abs(k))}
    </td>
  );
}

function Kpi({ label, value, hint, tone }) {
  return (
    <div className={`synth-kpi ${tone ? `synth-kpi--${tone}` : ''}`}>
      <span className="synth-kpi__label">{label}</span>
      <span className="synth-kpi__value">{value}</span>
      {hint && <span className="synth-kpi__hint">{hint}</span>}
    </div>
  );
}

export default function SynthesisTab({
  scenario,
  calc,
  revenues = [],
  variables = [],
  personnelProjection = null,
  chargesSocialesMonthly = null,
  assurancesMonthly = null,
  annualExtrasMonthly = null,
  bsLiquidityByYear = {},
  detailMode = false,
  horizon: horizonProp = 12,
  scenarios = [],
  compareIds = [],
  onCompareChange = null,
  compareSeries = [],
}) {
  const { t } = useTranslation();
  const p = useP();
  const [expandedPersonnel, setExpandedPersonnel] = useState(false);
  const [expandedRevenues, setExpandedRevenues] = useState(false);
  const [expandedVariables, setExpandedVariables] = useState(false);
  const [activeViewKey, setActiveViewKey] = useState('total');

  // ── Print : force all groups expanded for Cmd+P / print button ───────────
  // We snapshot the user's current expanded-state on beforeprint, force all
  // three to true so every sub-line appears in the PDF, then restore on
  // afterprint (the browser closes the print dialog).
  useEffect(() => {
    let snapshot = null;
    const onBefore = () => {
      snapshot = {
        rev: expandedRevenues,
        pers: expandedPersonnel,
        vars: expandedVariables,
      };
      setExpandedRevenues(true);
      setExpandedPersonnel(true);
      setExpandedVariables(true);
    };
    const onAfter = () => {
      if (!snapshot) return;
      setExpandedRevenues(snapshot.rev);
      setExpandedPersonnel(snapshot.pers);
      setExpandedVariables(snapshot.vars);
      snapshot = null;
    };
    window.addEventListener('beforeprint', onBefore);
    window.addEventListener('afterprint', onAfter);
    return () => {
      window.removeEventListener('beforeprint', onBefore);
      window.removeEventListener('afterprint', onAfter);
    };
  }, [expandedRevenues, expandedPersonnel, expandedVariables]);

  if (!calc) {
    return (
      <div>
        <h2 className="tres-section-title">Cash Outlook — {scenario?.name}</h2>
        <p className="tres-section-subtitle">
          {p('Calculation unavailable (empty scenario?).')}
        </p>
      </div>
    );
  }

  const activeView = VIEWS.find((v) => v.key === activeViewKey) || VIEWS[0];
  // Durée de projection : pilotée dans « Point de départ » (étape 2), reçue en prop.
  // Onglet « Détail » (top-level, après Prévision trésorerie) : tout déplié —
  // chaque sous-ligne produits encaissés / charges de personnel / achats visible.
  const showRev = detailMode || expandedRevenues;
  const showPers = detailMode || expandedPersonnel;
  const showVars = detailMode || expandedVariables;

  // Dérivation des séries / totaux / détails — partagée avec l'impression
  // (CashForecastPrint) via engine/cashSeries → chiffres identiques écran/PDF.
  const {
    horizon: H, soldes,
    startMonth, monthLabels, startYear, soldeInit,
    effectiveFluxNetMonthly, effectiveSoldeMonthly,
    soldeFinal, soldeMin, soldeMinLabel, burnRate, runway,
    chartData,
    totalEnc, totalEncHT, totalEncTVA, totalEncOpening,
    totalDecVariablesHT, totalDecMensuelHT, totalDecSpecifiqueHT,
    totalDecExpTVA, totalDecTva, totalDecOpening,
    salairesNets, csTotalMonthly, assurances,
    persSal, persCs, persAss,
    varCompMonthly, bonusMonthly, extrasMonthly, expensesMonthly,
    persVarComp, persBonus, persExtras, persExpenses, persOther,
    effectiveDecPers, effectiveTotalDecPers,
    decExploitMonthly, totalDecExploit, totalDec, totalFluxNet,
    hasOpeningRec, hasOpeningPay,
    revenueDetails, variableDetails,
  } = deriveCashSeries({
    scenario, calc, revenues, variables,
    personnelProjection, chargesSocialesMonthly, assurancesMonthly, annualExtrasMonthly,
    bsLiquidityByYear, horizon: horizonProp,
  });

  // Y a-t-il au moins un point de liquidité N-1 ? Sinon, pas de ligne ni légende.
  const hasN1 = chartData.some((d) => Number.isFinite(d.liqN1));

  // Comparaison de courbes : uniquement en vue Total (le solde d'un autre
  // scénario n'a de sens que là). Jusqu'à 2 scénarios, courbe de solde seule.
  const showCompare = activeViewKey === 'total';
  const cmpActive = showCompare ? compareSeries.slice(0, 2) : [];

  // Solde recalculé selon la vue active : cash initial + cumul des flux de la vue.
  // Pour 'total', le cumul de (enc − dec) reconstitue le solde réel. On y greffe
  // la série de solde des scénarios comparés (cmp0 / cmp1), alignée par mois.
  const viewData = (() => {
    let acc = soldeInit;
    return chartData.map((d, i) => {
      acc += viewNet(activeView, d);
      const extra = { vsolde: acc, vmanco: acc < 0 ? Math.abs(acc) : 0 };
      cmpActive.forEach((c, ci) => {
        const v = c.solde?.[i];
        if (Number.isFinite(v)) extra[`cmp${ci}`] = v;
      });
      return { ...d, ...extra };
    });
  })();
  const viewSoldeMin = Math.min(...viewData.map((d) => d.vsolde));

  // Styles des courbes comparées : couleurs franches distinctes du navy (courbe
  // principale), toujours en traitillé (amber puis bleu).
  const CMP_STYLES = [
    { color: '#B8862A', dash: '6 3' },
    { color: '#4A7BA5', dash: '2 4' },
  ];
  // Comparaison active → on masque la courbe « année passée » (N-1) : deux
  // références temporelles superposées prêtent à confusion.
  const comparing = cmpActive.length > 0;
  const otherScenarios = scenarios.filter((s) => s.id !== scenario?.id);
  const toggleCompare = (id) => {
    if (!onCompareChange) return;
    if (compareIds.includes(id)) onCompareChange(compareIds.filter((x) => x !== id));
    else if (compareIds.length < 2) onCompareChange([...compareIds, id]);
  };

  // KPIs revenus : total encaissé sur l'année N (calendaire) + moyenne mensuelle
  // sur la fenêtre de projection (H mois), cohérente avec le décaissement moyen.
  const numN = 12 - startMonth; // mois restants dans l'année N (startMonth=0 → 12 mois)
  const encNTotal = calc.soldes.slice(0, numN).reduce((a, s) => a + (Number(s.encHT) || 0), 0);
  const avgEncHT = totalEncHT / H;

  return (
    <div className="synth-tab">

      {/* ── Chart ────────────────────────────────────────────────────── */}
      {!detailMode && (<>
      <div className="synth-chart">
        <div className="synth-chart__filters">
          {VIEWS.map((v, i) => {
            const on = activeViewKey === v.key;
            return (
              <React.Fragment key={v.key}>
                {i === 1 && <span className="synth-filter-pill__sep" />}
                <button
                  type="button"
                  className={`synth-filter-pill${on ? ' is-active' : ''}`}
                  style={{
                    borderColor: on ? v.color : '#D6D3D1',
                    background: on ? `${v.color}20` : 'transparent',
                  }}
                  onClick={() => setActiveViewKey(v.key)}
                >
                  <span className="synth-filter-pill__dot" style={{ background: v.color }} />
                  {p(v.label)}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Comparer avec — jusqu'à 2 autres scénarios, vue Total uniquement. */}
        {showCompare && onCompareChange && otherScenarios.length > 0 && (
          <div className="synth-chart__compare">
            <span className="synth-chart__compare-lbl">{p('Comparer avec')}</span>
            {otherScenarios.map((s) => {
              const on = compareIds.includes(s.id);
              const idx = compareIds.indexOf(s.id);
              const disabled = !on && compareIds.length >= 2;
              const color = on && idx >= 0 ? CMP_STYLES[idx].color : '#B7C7CD';
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`synth-cmp-pill${on ? ' is-active' : ''}`}
                  style={{ borderColor: on ? color : '#D6D3D1', background: on ? `${color}18` : 'transparent' }}
                  onClick={() => toggleCompare(s.id)}
                  disabled={disabled}
                  title={disabled ? p('Maximum 2 scénarios comparés') : undefined}
                >
                  <span className="synth-cmp-pill__dot" style={{ background: color }} />
                  {s.type === 'snapshot' ? `📌 ${s.name}` : s.name}
                </button>
              );
            })}
          </div>
        )}

        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={viewData} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#EDE8DF" vertical={false} />
            <XAxis
              dataKey="monthShort"
              tick={{ fontSize: 11, fill: '#57534E' }}
              axisLine={{ stroke: '#D6D3D1' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#57534E' }}
              axisLine={{ stroke: '#D6D3D1' }}
              tickLine={false}
              tickFormatter={(v) => formatSwissAmount(Math.round(v / 1000))}
              width={68}
              label={{ value: 'kCHF', angle: -90, position: 'insideLeft', offset: 12, style: { fill: '#78716C', fontSize: 10 } }}
            />
            <ReferenceLine y={0} stroke="#78716C" strokeWidth={1} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const d = payload[0].payload;
                const net = viewNet(activeView, d);
                const soldeLabel = activeViewKey === 'total' ? p('Solde de trésorerie') : p('Solde (vue)');
                return (
                  <div className="synth-chart__tooltip">
                    <div className="synth-chart__tooltip-month">{d.label}</div>
                    {activeView.bars.map((b) => (
                      <div key={b.key} className="synth-chart__tooltip-row">
                        <span className="swatch" style={{ background: b.stroke, opacity: 0.8 }} />
                        <span>{p(b.name)}</span>
                        <span>{b.side === 'dec' ? '−' : ''}{fmtK(d[b.key])} k</span>
                      </div>
                    ))}
                    {activeView.bars.length > 1 && (
                      <div className="synth-chart__tooltip-row" style={{ fontWeight: 600 }}>
                        <span className="swatch" style={{ background: 'transparent' }} />
                        <span>{p('Flux net')}</span>
                        <span>{fmtK(net)} k</span>
                      </div>
                    )}
                    {Number.isFinite(d.liqN1) && activeViewKey === 'total' && !comparing && (
                      <div className="synth-chart__tooltip-row">
                        <span className="swatch swatch--flux" />
                        <span>{t('phrases:p_65a3c1b43a')}</span>
                        <span>{fmtK(d.liqN1)} k</span>
                      </div>
                    )}
                    {cmpActive.map((c, ci) => (
                      Number.isFinite(d[`cmp${ci}`]) ? (
                        <div key={c.id} className="synth-chart__tooltip-row">
                          <span className="swatch" style={{ background: CMP_STYLES[ci].color, opacity: 0.85 }} />
                          <span>{c.name}</span>
                          <span>{fmtK(d[`cmp${ci}`])} k</span>
                        </div>
                      ) : null
                    ))}
                    <div className="synth-chart__tooltip-total">
                      <span>{soldeLabel}</span>
                      <span>{fmtK(d.vsolde)} k</span>
                    </div>
                    {d.vmanco > 0 && (
                      <div className="synth-chart__tooltip-row" style={{ color: '#BA5D4A', fontWeight: 600 }}>
                        <span />
                        <span>{p('Découvert')}</span>
                        <span>−{fmtK(d.vmanco)} k</span>
                      </div>
                    )}
                  </div>
                );
              }}
            />
            {activeView.bars.map((b) => (
              <Bar
                key={b.key}
                dataKey={b.key}
                stackId={b.side}
                name={p(b.name)}
                fill={b.fill}
                fillOpacity={b.op}
                stroke={b.stroke}
                isAnimationActive={false}
              />
            ))}
            {hasN1 && activeViewKey === 'total' && !comparing && (
              <Line
                dataKey="liqN1"
                name={t('phrases:p_65a3c1b43a')}
                type="monotone"
                stroke="#2B203E"
                strokeWidth={1.8}
                strokeDasharray="5 4"
                strokeOpacity={0.65}
                dot={{ r: 2.5, fill: '#2B203E', fillOpacity: 0.65 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}
            {cmpActive.map((c, ci) => (
              <Line
                key={c.id}
                dataKey={`cmp${ci}`}
                name={c.name}
                type="monotone"
                stroke={CMP_STYLES[ci].color}
                strokeWidth={2}
                strokeDasharray={CMP_STYLES[ci].dash}
                strokeOpacity={0.85}
                dot={{ r: 2.5, fill: CMP_STYLES[ci].color }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            <Line
              dataKey="vsolde"
              name={t('phrases:p_2eb9bf52dd')}
              type="monotone"
              stroke="#2B203E"
              strokeWidth={2.5}
              dot={(props) => {
                const { cx, cy, payload, index } = props;
                const isNeg = payload?.vsolde < 0;
                return (
                  <circle
                    key={index}
                    cx={cx}
                    cy={cy}
                    r={isNeg ? 4 : 3}
                    fill={isNeg ? '#BA5D4A' : '#2B203E'}
                    stroke={isNeg ? '#BA5D4A' : '#2B203E'}
                  />
                );
              }}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="vmanco"
                content={(props) => {
                  const { x, y, value } = props;
                  const v = Number(value) || 0;
                  if (v <= 0) return null;
                  return (
                    <text
                      x={Number(x)}
                      y={Number(y) + 16}
                      textAnchor="middle"
                      fill="#BA5D4A"
                      fontSize={10}
                      fontFamily="JetBrains Mono, ui-monospace, monospace"
                      style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                    >
                      {`−${fmtK(v)} k`}
                    </text>
                  );
                }}
              />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
        <div className="synth-chart__legend">
          {activeView.bars.map((b) => (
            <span key={b.key}><span className="swatch" style={{ background: b.stroke, opacity: 0.8 }} /> {p(b.name)}</span>
          ))}
          <span><span className="swatch swatch--solde" /> {activeViewKey === 'total' ? p('Solde de trésorerie') : p('Solde (vue)')}</span>
          {cmpActive.map((c, ci) => (
            <span key={c.id}>
              <span className="swatch" style={{ background: CMP_STYLES[ci].color, opacity: 0.85 }} /> {c.name} {p('(comparé)')}
            </span>
          ))}
          {hasN1 && activeViewKey === 'total' && !comparing && (
            <span><span className="swatch swatch--flux" /> {p('Trésorerie N-1 (bilan, pointillé)')}</span>
          )}
          {viewSoldeMin < 0 && (
            <span style={{ color: '#BA5D4A', fontWeight: 600 }}>
              · {p('Plancher')} {fmtK(viewSoldeMin)} k
            </span>
          )}
        </div>
      </div>

      {/* ── KPI banner ──────────────────────────────────────────────── */}
      <div className="synth-kpis">
        <Kpi
          label={p('Solde initial')}
          value={`${fmtK(soldeInit)} k`}
          hint={t('phrases:p_07fd5ff7aa')}
          tone="navy"
        />
        <Kpi
          label={p('Solde final')}
          value={`${fmtK(soldeFinal)} k`}
          hint={t('phrases:p_e1f859f1f3')}
          tone={soldeFinal < 0 ? 'terra' : 'sage'}
        />
        <Kpi
          label={p('Solde minimum')}
          value={`${fmtK(soldeMin)} k`}
          hint={`${p('atteint en')} ${soldeMinLabel}`}
          tone={soldeMin < 0 ? 'terra' : 'navy'}
        />
        <Kpi
          label={t('phrases:p_c7dec733d4', { year: startYear })}
          value={`${fmtK(encNTotal)} k`}
          hint={`${monthLabels[0]}–${t('common:months.12')} ${startYear}`}
          tone="sage"
        />
        <Kpi
          label={p('Encaissement moyen')}
          value={`${fmtK(avgEncHT)} k`}
          hint={p('HT · moyenne mensuelle')}
          tone="sage"
        />
        <Kpi
          label={t('phrases:p_280bfa2283')}
          value={`${fmtK(burnRate)} k`}
          hint={p('moyenne mensuelle')}
          tone="navy"
        />
        <Kpi
          label={p('Autonomie')}
          value={`${runway.toFixed(1)} ${p('mois')}`}
          hint={p('solde initial / burn')}
          tone={runway < 6 ? 'terra' : 'navy'}
        />
      </div>

      {/* ── Cash flow plan (kCHF) ────────────────────────────────────── */}
      </>)}

      {detailMode && (
      <table className="synth-table">
        <thead>
          <tr>
            <th className="synth-table__row-label">{p('Plan de trésorerie · kCHF')}</th>
            {chartData.map((d, i) => (
              <th key={i} className="synth-table__month">
                {d.monthShort}
                <span className="synth-table__year">{d.year}</span>
              </th>
            ))}
            <th className="synth-table__total-col">{p('Total')}</th>
          </tr>
        </thead>
        <tbody>
          {/* Opening balance */}
          <tr className="synth-table__row synth-table__row--solde-debut">
            <td className="synth-table__row-label">{p("Solde d'ouverture")}</td>
            {effectiveSoldeMonthly.map((_, i) => {
              const v = i === 0 ? soldeInit : effectiveSoldeMonthly[i - 1];
              return <SignedCell key={i} value={v} tone="auto" />;
            })}
            <td className="synth-table__cell is-navy">{fmtK(soldeInit)}</td>
          </tr>

          {/* Revenue collected (excl. VAT) — expandable */}
          <tr
            className="synth-table__row synth-table__row--expandable"
            onClick={() => setExpandedRevenues((v) => !v)}
          >
            <td className="synth-table__row-label">
              <span className="synth-table__caret">{showRev ? '▾' : '▸'}</span>
              {' '}{p('Produits encaissés (HT)')}
            </td>
            {soldes.map((s, i) => (
              <NumCell key={i} value={s.encHT} tone="pos" />
            ))}
            <td className="synth-table__cell is-pos">{fmtK(totalEncHT)}</td>
          </tr>
          {showRev && revenueDetails.map((rev) => {
            const rowTotal = rev.monthly.reduce((a, b) => a + b, 0);
            return (
              <tr key={rev.id} className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{rev.label}</td>
                {rev.monthly.map((v, i) => (
                  <NumCell key={i} value={v} tone="pos" />
                ))}
                <td className="synth-table__cell is-pos">{fmtK(rowTotal)}</td>
              </tr>
            );
          })}
          <tr className="synth-table__row synth-table__row--italic">
            <td className="synth-table__row-label">{t('phrases:p_b7f6bbb3e5')}</td>
            {soldes.map((s, i) => (
              <NumCell key={i} value={s.encTVA} tone="amber" />
            ))}
            <td className="synth-table__cell is-amber">{fmtK(totalEncTVA)}</td>
          </tr>
          {hasOpeningRec && (
            <tr className="synth-table__row">
              <td className="synth-table__row-label">{p('Créances clients ouvertes')}</td>
              {soldes.map((s, i) => (
                <NumCell key={i} value={s.encOpening} tone="pos" />
              ))}
              <td className="synth-table__cell is-pos">{fmtK(totalEncOpening)}</td>
            </tr>
          )}
          <tr className="synth-table__row synth-table__row--subtotal synth-table__row--enc-total">
            <td className="synth-table__row-label">{p('Total encaissements (TVA incl.)')}</td>
            {soldes.map((s, i) => (
              <NumCell key={i} value={s.enc} tone="pos" />
            ))}
            <td className="synth-table__cell is-pos">{fmtK(totalEnc)}</td>
          </tr>

          {/* Charges de personnel — salaires + CS + assurances + extras + frais (expandable) */}
          <tr
            className="synth-table__row synth-table__row--expandable"
            onClick={() => setExpandedPersonnel((v) => !v)}
          >
            <td className="synth-table__row-label">
              <span className="synth-table__caret">{showPers ? '▾' : '▸'}</span>
              {' '}{p('Charges de personnel')}
            </td>
            {effectiveDecPers.map((v, i) => (
              <SignedCell key={i} value={-(v + num(extrasMonthly[i]) + num(expensesMonthly[i]))} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(effectiveTotalDecPers + persExtras + persExpenses)}</td>
          </tr>
          {showPers && (
            <>
              <tr className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{t('phrases:p_b03c04eee5')}</td>
                {salairesNets.map((v, i) => (
                  <SignedCell key={i} value={-num(v)} tone="neg" />
                ))}
                <td className="synth-table__cell is-neg">-{fmtK(persSal)}</td>
              </tr>
              <tr className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{t('phrases:p_61bfef5eb5')}</td>
                {csTotalMonthly.map((v, i) => (
                  <SignedCell key={i} value={-num(v)} tone="neg" />
                ))}
                <td className="synth-table__cell is-neg">-{fmtK(persCs)}</td>
              </tr>
              <tr className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{p('Assurances (LPP / LAA / IJM)')}</td>
                {assurances.map((v, i) => (
                  <SignedCell key={i} value={-num(v)} tone="neg" />
                ))}
                <td className="synth-table__cell is-neg">-{fmtK(persAss)}</td>
              </tr>
              {Math.abs(persOther) > 0.5 && (
                <tr className="synth-table__row synth-table__row--subdetail synth-table__row--italic">
                  <td className="synth-table__row-label synth-table__row-label--subdetail">{p('Autres')}</td>
                  {soldes.map((s, i) => {
                    const v = num(s.decPers) - num(salairesNets[i]) - num(csTotalMonthly[i]) - num(assurances[i]);
                    return <SignedCell key={i} value={-v} tone="neg" />;
                  })}
                  <td className="synth-table__cell is-neg">-{fmtK(persOther)}</td>
                </tr>
              )}
              <tr className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{p('Extras (rémunération suppl.)')}</td>
                {extrasMonthly.map((v, i) => (
                  <SignedCell key={i} value={-num(v)} tone="neg" />
                ))}
                <td className="synth-table__cell is-neg">-{fmtK(persExtras)}</td>
              </tr>
              <tr className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{p('Frais et débours')}</td>
                {expensesMonthly.map((v, i) => (
                  <SignedCell key={i} value={-num(v)} tone="neg" />
                ))}
                <td className="synth-table__cell is-neg">-{fmtK(persExpenses)}</td>
              </tr>
            </>
          )}
          <tr
            className="synth-table__row synth-table__row--expandable"
            onClick={() => setExpandedVariables((v) => !v)}
          >
            <td className="synth-table__row-label">
              <span className="synth-table__caret">{showVars ? '▾' : '▸'}</span>
              {' '}{p('Achats & charges variables')}
            </td>
            {soldes.map((s, i) => (
              <SignedCell key={i} value={-num(s.decVariablesHT)} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(totalDecVariablesHT)}</td>
          </tr>
          {showVars && variableDetails.map((v) => {
            const rowTotal = v.monthly.reduce((a, b) => a + b, 0);
            return (
              <tr key={v.id} className="synth-table__row synth-table__row--subdetail">
                <td className="synth-table__row-label synth-table__row-label--subdetail">{v.label}</td>
                {v.monthly.map((x, i) => (
                  <SignedCell key={i} value={-x} tone="neg" />
                ))}
                <td className="synth-table__cell is-neg">-{fmtK(rowTotal)}</td>
              </tr>
            );
          })}
          <tr className="synth-table__row">
            <td className="synth-table__row-label">{p('Charges fixes récurrentes')}</td>
            {soldes.map((s, i) => (
              <SignedCell key={i} value={-num(s.decMensuelHT)} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(totalDecMensuelHT)}</td>
          </tr>
          <tr className="synth-table__row">
            <td className="synth-table__row-label">{p('Charges non récurrentes')}</td>
            {soldes.map((s, i) => (
              <SignedCell key={i} value={-num(s.decSpecifiqueHT)} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(totalDecSpecifiqueHT)}</td>
          </tr>
          <tr className="synth-table__row synth-table__row--italic">
            <td className="synth-table__row-label">{p('TVA déductible (fournisseurs)')}</td>
            {soldes.map((s, i) => (
              <SignedCell key={i} value={-num(s.decExpTVA)} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(totalDecExpTVA)}</td>
          </tr>
          <tr className="synth-table__row synth-table__row--italic">
            <td className="synth-table__row-label">{p("TVA nette versée à l'AFC")}</td>
            {soldes.map((s, i) => (
              <SignedCell key={i} value={-num(s.decTva)} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(totalDecTva)}</td>
          </tr>
          {hasOpeningPay && (
            <tr className="synth-table__row">
              <td className="synth-table__row-label">{p('Dettes ouvertes')}</td>
              {soldes.map((s, i) => (
                <SignedCell key={i} value={-num(s.decOpening)} tone="neg" />
              ))}
              <td className="synth-table__cell is-neg">-{fmtK(totalDecOpening)}</td>
            </tr>
          )}
          <tr className="synth-table__row synth-table__row--subtotal">
            <td className="synth-table__row-label">{p("Total décaissements d'exploitation")}</td>
            {decExploitMonthly.map((v, i) => (
              <SignedCell key={i} value={-num(v)} tone="neg" />
            ))}
            <td className="synth-table__cell is-neg">-{fmtK(totalDecExploit)}</td>
          </tr>
          <tr className="synth-table__row synth-table__row--subtotal synth-table__row--dec-total">
            <td className="synth-table__row-label">{t('phrases:p_4bfd386410')}</td>
            {soldes.map((s, i) => {
              const v = num(decExploitMonthly[i]) + num(s.decTva);
              return <SignedCell key={i} value={-v} tone="neg" />;
            })}
            <td className="synth-table__cell is-neg">-{fmtK(totalDec)}</td>
          </tr>

          {/* Net cash flow */}
          <tr className="synth-table__row synth-table__row--fluxnet">
            <td className="synth-table__row-label">{p('Flux net de trésorerie')}</td>
            {effectiveFluxNetMonthly.map((v, i) => (
              <SignedCell key={i} value={v} tone="auto" showSign />
            ))}
            <td className={`synth-table__cell ${totalFluxNet < 0 ? 'is-neg' : 'is-pos'}`}>
              {totalFluxNet < 0 ? '-' : ''}{fmtK(Math.abs(totalFluxNet))}
            </td>
          </tr>

          {/* Closing balance */}
          <tr className="synth-table__row synth-table__row--solde-fin">
            <td className="synth-table__row-label">{t('phrases:p_0b59c4dd16')}</td>
            {effectiveSoldeMonthly.map((v, i) => (
              <SignedCell key={i} value={v} tone="auto" />
            ))}
            <td className={`synth-table__cell ${soldeFinal < 0 ? 'is-neg' : 'is-pos'}`}>
              {soldeFinal < 0 ? '-' : ''}{fmtK(Math.abs(soldeFinal))}
            </td>
          </tr>
        </tbody>
      </table>
      )}
    </div>
  );
}
